import { decodeCompletionId } from '../lib/completion-id'
import { isScheduleKind } from '../types'
import { createDefaults, defaultSettings } from './defaults'
import type { AppData, AppSettings, Completion, CustomTask, Member } from './types'

/**
 * Normalise anything sitting in `localStorage` under `maison_zen_v1` into the
 * current shape.
 *
 * The input is untrusted: it may come from any version of the app, from a
 * hand-edited value, or from a partially written save. Every field is read
 * defensively and every collection defaults to empty rather than throwing —
 * losing one malformed record beats refusing to open the app at all.
 *
 * It is **synchronous**, which is why it cannot read the draft's data. That
 * data lives in IndexedDB, behind an async API, and bridging the two would mean
 * a second migration path with its own failure mode. Instead `0003` copies the
 * legacy rows server-side and the first sync reconstructs the local store from
 * `completions`. That only works in that order: migration first, then the
 * client deploy.
 */

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

function str(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback
}

function bool(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback
}

/**
 * A whole number, or the fallback.
 *
 * A saved value out of range is left alone rather than clamped: `buildPlan` is
 * where a schedule slot is decided, and it drops a chore it cannot place. Two
 * places quietly repairing the same value in different ways is how a chore ends
 * up on a day nobody chose.
 */
function int(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.trunc(value) : fallback
}

/** An ISO timestamp, or the epoch — which loses every comparison it takes part in. */
function timestamp(value: unknown): string {
  const text = str(value)
  return text && !Number.isNaN(new Date(text).getTime()) ? text : '1970-01-01T00:00:00.000Z'
}

function nullableTimestamp(value: unknown): string | null {
  const text = str(value)
  if (!text) return null
  return Number.isNaN(new Date(text).getTime()) ? null : text
}

function normalizeCompletion(value: unknown): Completion | null {
  const row = asRecord(value)
  const id = str(row['id']).trim()
  if (!id) return null

  // The three components are stored as fields *and* encoded in the id. When a
  // save predates one of the fields, the id is the authority: it is what both
  // devices upsert on, so a field disagreeing with it would push a row that
  // contradicts its own key.
  const decoded = decodeCompletionId(id)
  if (!decoded) return null

  return {
    id,
    taskDate: decoded.taskDate,
    taskId: decoded.taskId,
    memberId: decoded.memberId,
    done: bool(row['done']),
    updatedAt: timestamp(row['updatedAt']),
    deletedAt: nullableTimestamp(row['deletedAt']),
    dirty: bool(row['dirty']),
  }
}

/**
 * One household-defined chore.
 *
 * An id is required for the reason it always is — it is the row's identity on
 * both devices — and a label is required because a chore with nothing to read
 * is a row the user can neither recognise nor delete. Everything else has a
 * defensible default.
 */
function normalizeTask(value: unknown): CustomTask | null {
  const row = asRecord(value)
  const id = str(row['id']).trim()
  const label = str(row['label']).trim()
  if (!id || !label) return null

  return {
    id,
    label,
    emoji: str(row['emoji']),
    category: str(row['category']),
    // Absent in every save written before members existed, and '' is exactly
    // what that install meant: a chore for the whole house.
    assigneeId: str(row['assigneeId']),
    schedule: isScheduleKind(row['schedule']) ? row['schedule'] : 'daily',
    weekday: int(row['weekday']),
    cycleWeek: int(row['cycleWeek']),
    onDate: str(row['onDate']),
    updatedAt: timestamp(row['updatedAt']),
    deletedAt: nullableTimestamp(row['deletedAt']),
    dirty: bool(row['dirty']),
  }
}

/**
 * One person in the household.
 *
 * An id is required for the reason it always is — it is the row's identity on
 * both devices, and here also the third component of every completion id that
 * person has ever written. A display name is required because a member with
 * nothing to read is a chip nobody can pick and a row the owner cannot find
 * again to remove.
 *
 * `user_id` is not read even if a hand-edited save contains one: it is not part
 * of this model, it never crosses the wire, and reading it here would be the
 * first step towards sending it.
 */
function normalizeMember(value: unknown): Member | null {
  const row = asRecord(value)
  const id = str(row['id']).trim()
  const displayName = str(row['displayName']).trim()
  if (!id || !displayName) return null

  return {
    id,
    displayName,
    username: str(row['username']).trim().toLowerCase(),
    authEmail: str(row['authEmail']).trim().toLowerCase(),
    role: str(row['role'], 'child') || 'child',
    updatedAt: timestamp(row['updatedAt']),
    deletedAt: nullableTimestamp(row['deletedAt']),
    dirty: bool(row['dirty']),
  }
}

function normalizeSettings(value: unknown): AppSettings {
  const row = asRecord(value)
  const fallback = defaultSettings()

  return {
    reminderEnabled: bool(row['reminderEnabled'], fallback.reminderEnabled),
    reminderTime: str(row['reminderTime'], fallback.reminderTime) || fallback.reminderTime,
    timezone: str(row['timezone'], fallback.timezone) || fallback.timezone,
    // A save with no envelope predates the settings row being synced. It reads
    // back at the epoch and clean, so the household's own hour wins the first
    // merge — the same rule a fresh install follows.
    updatedAt: timestamp(row['updatedAt']),
    deletedAt: nullableTimestamp(row['deletedAt']),
    dirty: bool(row['dirty']),
  }
}

export function normalizeAppData(persisted: unknown): AppData {
  if (persisted === null || persisted === undefined) return createDefaults()

  const data = asRecord(persisted)

  // Later rows win on a duplicate id, and a duplicate can only come from a
  // hand-edited save: the store keys on id everywhere else.
  const byId = new Map<string, Completion>()
  for (const raw of asArray(data['completions'])) {
    const row = normalizeCompletion(raw)
    if (row) byId.set(row.id, row)
  }

  const tasksById = new Map<string, CustomTask>()
  for (const raw of asArray(data['tasks'])) {
    const row = normalizeTask(raw)
    if (row) tasksById.set(row.id, row)
  }

  const membersById = new Map<string, Member>()
  for (const raw of asArray(data['members'])) {
    const row = normalizeMember(raw)
    if (row) membersById.set(row.id, row)
  }

  return {
    completions: [...byId.values()],
    // Absent in every save written before custom chores existed, which reads
    // back as an empty list — the household then has the built-in plan and
    // nothing else, which is exactly what it had.
    tasks: [...tasksById.values()],
    // Absent in every save written before members existed. Empty is safe rather
    // than merely tolerable: the member picker falls back to the signed-in
    // member alone, and the first sync fills the list in from the household.
    members: [...membersById.values()],
    settings: normalizeSettings(data['settings']),
  }
}
